import React, { useMemo, useEffect, useState, useContext } from 'react';
import { string, number, array } from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames/bind';
import useStyles from 'hooks/useStyles';

import history from 'history.js';

import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';

import { push, goBack } from 'reduxModules/navigation/action';

import { getCourseById } from 'reduxModules/course/selector';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';

import { getPreviousUrlFromStorage } from '@snapask/common-components/dist/utils/history';
import {
  COURSE_DETAIL_ENTER,
  COURSE_RECOMMENDATION_CLICK,
  COURSE_PURCHASE_CLICK,
  COURSE_LESSONPAGE_ENTER,
} from 'constants/gtmEvent';

import {
  COURSE,
  SINGLE_PURCHASE_TEMPLATE,
  COURSE_LEARNING_LESSON_TEMPLATE,
} from 'constants/routingPath';

import * as upsellBannerPage from 'constants/upsellBannerPage';

import ApplicationContext from 'components/ApplicationContext';
import CourseContext from 'routes/course/components/CourseContext';

import { BackIcon, StarIcon } from 'components/Icon';
import SnapaskHeader from 'components/SnapaskHeader';
import SnapaskFooter from 'components/SnapaskFooter';
import SnapaskLocaleSelection from 'components/SnapaskLocaleSelection';
import CollapseContent from 'components/CollapseContent';
import UpsellBanner from 'components/UpsellBanner';
import AlertBanner from 'components/AlertBanner';
import PaymentOptionList from 'components/PaymentOptionList';
import DesktopBreadcrumb from 'components/DesktopBreadcrumb';
import MobileBreadcrumb from 'components/MobileBreadcrumb';
import GetFreeTrialButton from 'components/GetFreeTrialButton';

import useUpsellBanner from 'hooks/useUpsellBanner';
import useGetCurrentImageType from 'hooks/useGetCurrentImageType';
import * as contentTypes from 'constants/contentTypes';

import { getContentMainCheckoutCollection } from 'reduxModules/checkoutCollection/selector';
import useCoursePlaylist from 'routes/course/hooks/useCoursePlaylist';
import CourseIntroCard from 'routes/course/components/CourseIntroCard';
import Notebook from '../components/Notebook';
import CourseSwiper from '../components/CourseSwiper';
import VideoPlayList from '../components/VideoPlayList';
import PurchaseButton from './components/PurchaseButton';
import Info from './components/Info';
import PlayListAndPaymentList from '../components/PlayListAndPaymentList';
import PayWallModal from '../components/PayWallModal';

import courseSwiperTheme from '../components/CourseSwiper/courseSwiperTheme.scss';
import commonStyles from '../common.scss';
import styles from './Course.scss';

const cx = classnames.bind(styles);

function Course({ id, lessonId, breadcrumbList }) {
  useStyles(styles, commonStyles, courseSwiperTheme);
  const { location } = history;

  const { imageType, setContainerRef } = useGetCurrentImageType(true);

  const { t } = useTranslation(['course', 'navbar', 'common']);
  const dispatch = useDispatch();
  const [stickyBottomBoundary, setStickyBottomBoundary] = useState(0);
  const {
    screenWidth: { isMobile, isTablet },
  } = useContext(ApplicationContext);

  const courseInfo = useSelector(getCourseById(id));
  const playlistInfo = useCoursePlaylist({
    ...courseInfo,
    initialLessonId: lessonId,
    isCoursePage: !lessonId,
  });
  const { upsellBannerVisible, hideUpsellBanner } = useUpsellBanner();

  const isLessonPage = !!lessonId;

  const {
    checkoutCollection,
    isPreOrdered,
    lessonMeta: { totalDurationInSeconds },
    lessonsAmount,
    nextLessonForPurchasedUser,
    notebook,
    relatedCourses,
    tag,
    testimonials,
    title,
    userPurchased,
    lessonList,
  } = courseInfo;

  const getOfficialSiteURL = useGetOfficialSiteURL();
  const courseHomeURL = getOfficialSiteURL(COURSE);
  const isTabletOrMobile = isMobile || isTablet;
  const bundleViewMode = location?.hash === '#view';

  const showTestimonialSection = testimonials.length > 0;
  const showPromotion =
    !!checkoutCollection && !userPurchased && !isPreOrdered && !bundleViewMode;
  const showContinueLearningSection = userPurchased || isPreOrdered;

  const mainCheckoutCollection = useSelector(
    getContentMainCheckoutCollection(contentTypes.COURSE, id),
  );

  const currentLesson = lessonList.find(lesson => lesson.id === lessonId);

  const courseEventTrackData = useMemo(
    () => ({
      courseId: id,
      courseName: title,
      totalLesson: lessonsAmount,
      totalLessonDuration: totalDurationInSeconds,
      tag,
      purchaseStatus: userPurchased,
      price: checkoutCollection?.displayPrice?.sellingPrice,
      priceCurrency: checkoutCollection?.displayCurrency,
    }),
    [
      id,
      title,
      lessonsAmount,
      totalDurationInSeconds,
      tag,
      userPurchased,
      checkoutCollection,
    ],
  );

  const courseLessonEventTrackData = useMemo(
    () => ({
      courseId: id,
      courseName: title,
      lessonId: currentLesson?.id,
      lessonName: currentLesson?.name,
      freeLesson: currentLesson?.isFree,
    }),
    [id, title, currentLesson],
  );

  const courseLearningPageUrl = useMemo(
    () =>
      getOfficialSiteURL(COURSE_LEARNING_LESSON_TEMPLATE, {
        courseId: id,
        lessonId,
      }),
    [getOfficialSiteURL, id, lessonId],
  );

  useEffect(() => {
    if (currentLesson?.playInLearningPage) {
      dispatch(push(courseLearningPageUrl));
    }
  }, [currentLesson, dispatch, courseLearningPageUrl, id, lessonId]);

  useEffect(() => {
    if (!isLessonPage) {
      dispatch(
        sendGTMEvent({
          eventName: COURSE_DETAIL_ENTER,
          data: courseEventTrackData,
        }),
      );
    }
  }, [isLessonPage, dispatch, courseEventTrackData]);

  useEffect(() => {
    if (isLessonPage) {
      dispatch(
        sendGTMEvent({
          eventName: COURSE_LESSONPAGE_ENTER,
          data: courseLessonEventTrackData,
        }),
      );
    }
  }, [isLessonPage, dispatch, courseLessonEventTrackData]);

  const handleGoPurchase = (section, shouldRedirectToCheckout = true) => {
    dispatch(
      sendGTMEvent({
        eventName: COURSE_PURCHASE_CLICK,
        data: {
          ...courseEventTrackData,
          section,
          eventName: COURSE_PURCHASE_CLICK.toLowerCase(),
        },
      }),
    );

    if (shouldRedirectToCheckout) {
      dispatch(
        push(
          getOfficialSiteURL(
            `${SINGLE_PURCHASE_TEMPLATE}#from=${COURSE}:${id}`,
            {
              id: mainCheckoutCollection.id,
            },
          ),
        ),
      );
    }
  };

  const goCourseLearningPage = () =>
    dispatch(
      push(
        getOfficialSiteURL(COURSE_LEARNING_LESSON_TEMPLATE, {
          courseId: id,
          lessonId: nextLessonForPurchasedUser.id,
        }),
      ),
    );

  const handleClickBack = () => {
    const previousUrl = getPreviousUrlFromStorage();
    if (previousUrl) {
      return dispatch(goBack());
    }

    return dispatch(push(courseHomeURL));
  };

  const renderTestimonialItem = (
    { name, rating, content },
    testimonialIndex,
  ) => (
    <li className={styles.item} key={testimonialIndex + 1}>
      <div className={styles.name}>{name}</div>
      <div className={styles.rating}>
        {Array.from({ length: rating }, (_, index) => (
          <StarIcon key={index} width={20} height={20} />
        ))}
      </div>
      <CollapseContent
        key={id}
        className={styles.content}
        maxLines={3}
        expandText={t('course:videoplayer.more')}
      >
        {content}
      </CollapseContent>
    </li>
  );

  const getStickyBottomBoundary = ref => {
    if (ref) {
      const { bottom } = ref.getBoundingClientRect();
      if (bottom > stickyBottomBoundary) {
        setStickyBottomBoundary(bottom);
      }
    }
  };

  const renderBreadcrumb = () =>
    isMobile ? (
      <MobileBreadcrumb
        className={styles.breadcrumb}
        path={breadcrumbList[breadcrumbList.length - 1]}
      />
    ) : (
      <DesktopBreadcrumb
        className={styles.breadcrumb}
        pathList={breadcrumbList}
      />
    );

  useEffect(() => {
    return setStickyBottomBoundary(0);
  }, []);

  return (
    <>
      <SnapaskHeader id="header" />
      <AlertBanner />
      <SnapaskLocaleSelection />
      {upsellBannerVisible && (
        <UpsellBanner
          page={upsellBannerPage.COURSE}
          onClose={hideUpsellBanner}
        />
      )}
      <section>
        <CourseContext.Provider
          value={{
            ...playlistInfo,
            ...courseInfo,
            courseEventTrackData,
            courseLessonEventTrackData,
          }}
        >
          <div className={styles.containerUnderHeader}>
            {bundleViewMode ? (
              <div className={styles.headerBack} onClick={handleClickBack}>
                <BackIcon width={24} height={24} />
                <h5>
                  {t(
                    'purchase:purchase.content.bundlePage.bundleDetailBackCta',
                  )}
                </h5>
              </div>
            ) : (
              renderBreadcrumb()
            )}
            {isLessonPage && (
              <h1 className={styles.title}>
                {playlistInfo.currentLesson.title}
              </h1>
            )}
          </div>
          <div
            className={cx(styles.courseContainer, {
              containerUnderHeader: !isTabletOrMobile,
            })}
          >
            <div className={styles.leftContent}>
              <div>
                <VideoPlayList
                  {...courseInfo}
                  goCourseLearningPage={goCourseLearningPage}
                  cta={
                    showPromotion && (
                      <PurchaseButton
                        hasAddOn={checkoutCollection.hasAddOn}
                        price={checkoutCollection?.displayPrice?.sellingPrice}
                        onClick={() => handleGoPurchase('video')}
                      />
                    )
                  }
                />

                {showPromotion && (
                  <div className={styles.paymentOptionList}>
                    <PaymentOptionList
                      contentType={contentTypes.COURSE}
                      contentId={id}
                      onClickCtaButton={() =>
                        handleGoPurchase('course_detail', false)
                      }
                    />
                    <GetFreeTrialButton
                      reversed
                      block
                      isShow={isMobile || isTablet}
                      type="course"
                      id={id}
                      name={title}
                      section="course_detail"
                    />
                  </div>
                )}
              </div>
              <div ref={getStickyBottomBoundary}>
                <Info
                  showPromotion={showPromotion}
                  showContinueLearningSection={showContinueLearningSection}
                  goCourseLearningPage={goCourseLearningPage}
                  {...courseInfo}
                  lessonId={lessonId}
                />
              </div>
            </div>
            <PlayListAndPaymentList
              isShowPaymentList={!isTabletOrMobile}
              isLessonPage={isLessonPage}
              showPromotion={showPromotion}
              showContinueLearningSection={showContinueLearningSection}
              goCourseLearningPage={goCourseLearningPage}
              stickyBottomBoundary={stickyBottomBoundary}
            />
          </div>
          <PayWallModal onPurchaseButtonClick={handleGoPurchase} />
        </CourseContext.Provider>
      </section>

      {notebook && (
        <section className={styles.notebook}>
          <div className={commonStyles.container}>
            <Notebook
              {...notebook}
              buttonText={t('course:features.note_cta0')}
              onButtonClick={() => handleGoPurchase('notebook')}
            />
          </div>
        </section>
      )}

      {showTestimonialSection && (
        <section className={commonStyles.container}>
          <h3 className={commonStyles.heading}>
            {t('course:features.testimonial')}
          </h3>
          <ul className={styles.testimonials}>
            {testimonials.map(renderTestimonialItem)}
          </ul>
        </section>
      )}
      <section
        className={cx(courseSwiperTheme.container, styles.courseSwiperWrapper)}
      >
        <h3 className={courseSwiperTheme.heading}>
          {t('course:features.more')}
        </h3>
        <CourseSwiper
          id="course-related-swiper"
          handleGetSwiperRef={ref => setContainerRef(ref.el)}
          slideDataList={relatedCourses}
          renderSlide={(props, index) => (
            <CourseIntroCard
              {...props}
              // eslint-disable-next-line react/prop-types
              pictureUrl={props.picture[imageType]}
              eventName={COURSE_RECOMMENDATION_CLICK}
              eventData={{ itemIndex: index }}
            />
          )}
        />
      </section>
      <SnapaskFooter />
    </>
  );
}

Course.propTypes = {
  id: string.isRequired,
  breadcrumbList: array.isRequired,
  lessonId: number,
};

Course.defaultProps = {
  lessonId: undefined,
};

export default Course;
